import { createTheme, alpha } from '@mui/material/styles';
import { lightScheme, darkScheme } from './colors';

/**
 * Material Design 3 Theme для MUI
 */

const scheme = lightScheme; // Можно переключить на darkScheme

// MD3 Typography Scale
const typography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  
  // Display
  displayLarge: {
    fontSize: '3.5625rem', // 57px
    fontWeight: 400,
    lineHeight: 1.12,
    letterSpacing: '-0.25px',
  },
  displayMedium: {
    fontSize: '2.8125rem', // 45px
    fontWeight: 400,
    lineHeight: 1.16,
  },
  displaySmall: {
    fontSize: '2.25rem', // 36px
    fontWeight: 400,
    lineHeight: 1.22,
  },
  
  // Headline
  headlineLarge: {
    fontSize: '2rem', // 32px
    fontWeight: 400,
    lineHeight: 1.25,
  },
  headlineMedium: {
    fontSize: '1.75rem', // 28px
    fontWeight: 400,
    lineHeight: 1.29,
  },
  headlineSmall: {
    fontSize: '1.5rem', // 24px
    fontWeight: 400,
    lineHeight: 1.33,
  },
  
  // Title
  titleLarge: {
    fontSize: '1.375rem', // 22px
    fontWeight: 500,
    lineHeight: 1.27,
  },
  titleMedium: {
    fontSize: '1rem', // 16px
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.15px',
  },
  titleSmall: {
    fontSize: '0.875rem', // 14px
    fontWeight: 500,
    lineHeight: 1.43,
    letterSpacing: '0.1px',
  },
  
  // Body
  bodyLarge: {
    fontSize: '1rem', // 16px
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.5px',
  },
  bodyMedium: {
    fontSize: '0.875rem', // 14px
    fontWeight: 400,
    lineHeight: 1.43,
    letterSpacing: '0.25px',
  },
  bodySmall: {
    fontSize: '0.75rem', // 12px
    fontWeight: 400,
    lineHeight: 1.33,
    letterSpacing: '0.4px',
  },
  
  // Label
  labelLarge: {
    fontSize: '0.875rem', // 14px
    fontWeight: 500,
    lineHeight: 1.43,
    letterSpacing: '0.1px',
  },
  labelMedium: {
    fontSize: '0.75rem', // 12px
    fontWeight: 500,
    lineHeight: 1.33,
    letterSpacing: '0.5px',
  },
  labelSmall: {
    fontSize: '0.6875rem', // 11px
    fontWeight: 500,
    lineHeight: 1.45,
    letterSpacing: '0.5px',
  },
  
  // Mapping to MUI defaults
  h1: {
    fontSize: '2.25rem',
    fontWeight: 500,
    lineHeight: 1.22,
  },
  h2: {
    fontSize: '1.75rem',
    fontWeight: 500,
    lineHeight: 1.29,
  },
  h3: {
    fontSize: '1.5rem',
    fontWeight: 500,
    lineHeight: 1.33,
  },
  h4: {
    fontSize: '1.375rem',
    fontWeight: 500,
    lineHeight: 1.27,
  },
  h5: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.43,
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.15px',
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.43,
    letterSpacing: '0.1px',
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.5px',
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.43,
    letterSpacing: '0.25px',
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.43,
    letterSpacing: '0.1px',
    textTransform: 'none',
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.33,
    letterSpacing: '0.4px',
  },
  overline: {
    fontSize: '0.6875rem',
    fontWeight: 500,
    lineHeight: 1.45,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
};

// MD3 Shape System
const shape = {
  borderRadius: 16, // Default medium
  borderRadiusNone: 0,
  borderRadiusExtraSmall: 4,
  borderRadiusSmall: 8,
  borderRadiusMedium: 12,
  borderRadiusLarge: 16,
  borderRadiusExtraLarge: 28,
  borderRadiusFull: 9999,
};

// MD3 Elevation (используем tonal elevation вместо теней)
const elevationShadows = [
  'none',
  `0px 1px 3px 1px ${alpha(scheme.shadow, 0.15)}, 0px 1px 2px 0px ${alpha(scheme.shadow, 0.3)}`,
  `0px 2px 6px 2px ${alpha(scheme.shadow, 0.15)}, 0px 1px 2px 0px ${alpha(scheme.shadow, 0.3)}`,
  `0px 4px 8px 3px ${alpha(scheme.shadow, 0.15)}, 0px 1px 3px 0px ${alpha(scheme.shadow, 0.3)}`,
  `0px 6px 10px 4px ${alpha(scheme.shadow, 0.15)}, 0px 2px 3px 0px ${alpha(scheme.shadow, 0.3)}`,
  `0px 8px 12px 6px ${alpha(scheme.shadow, 0.15)}, 0px 4px 4px 0px ${alpha(scheme.shadow, 0.3)}`,
  ...Array(19).fill(`0px 8px 12px 6px ${alpha(scheme.shadow, 0.15)}`),
];

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: scheme.primary,
      light: scheme.primaryContainer,
      dark: scheme.onPrimaryContainer,
      contrastText: scheme.onPrimary,
    },
    secondary: {
      main: scheme.secondary,
      light: scheme.secondaryContainer,
      dark: scheme.onSecondaryContainer,
      contrastText: scheme.onSecondary,
    },
    error: {
      main: scheme.error,
      light: scheme.errorContainer,
      dark: scheme.onErrorContainer,
      contrastText: scheme.onError,
    },
    warning: {
      main: scheme.warning,
      light: scheme.warningContainer,
      dark: scheme.onWarningContainer,
      contrastText: scheme.onWarning,
    },
    success: {
      main: scheme.success,
      light: scheme.successContainer,
      dark: scheme.onSuccessContainer,
      contrastText: scheme.onSuccess,
    },
    info: {
      main: scheme.tertiary,
      light: scheme.tertiaryContainer,
      dark: scheme.onTertiaryContainer,
      contrastText: scheme.onTertiary,
    },
    background: {
      default: scheme.background,
      paper: scheme.surface,
    },
    text: {
      primary: scheme.onSurface,
      secondary: scheme.onSurfaceVariant,
      disabled: alpha(scheme.onSurface, 0.38),
    },
    divider: scheme.outlineVariant,
    action: {
      active: scheme.onSurfaceVariant,
      hover: alpha(scheme.onSurface, 0.08),
      selected: alpha(scheme.primary, 0.12),
      disabled: alpha(scheme.onSurface, 0.12),
      disabledBackground: alpha(scheme.onSurface, 0.12),
      focus: alpha(scheme.onSurface, 0.12),
    },
    // Custom MD3 colors
    surfaceVariant: scheme.surfaceVariant,
    outline: scheme.outline,
    outlineVariant: scheme.outlineVariant,
    surfaceContainerLowest: scheme.surfaceContainerLowest,
    surfaceContainerLow: scheme.surfaceContainerLow,
    surfaceContainer: scheme.surfaceContainer,
    surfaceContainerHigh: scheme.surfaceContainerHigh,
    surfaceContainerHighest: scheme.surfaceContainerHighest,
  },
  
  typography,
  shape,
  shadows: elevationShadows,
  
  components: {
    // Кастомные MD3-варианты типографики (displayX/headlineX/titleX/bodyX/labelX)
    // не имеют встроенного variantMapping → MUI по умолчанию рендерит их как
    // инлайновый <span>, из-за чего соседние Typography «склеиваются» в одну строку
    // (например title + subtitle в StatCard). Явно маппим их на блочные элементы.
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          displayLarge: 'h1', displayMedium: 'h1', displaySmall: 'h2',
          headlineLarge: 'h2', headlineMedium: 'h3', headlineSmall: 'h4',
          titleLarge: 'h5', titleMedium: 'h6', titleSmall: 'h6',
          bodyLarge: 'p', bodyMedium: 'p', bodySmall: 'p',
          labelLarge: 'p', labelMedium: 'p', labelSmall: 'p',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          height: '100%',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        body: {
          height: '100%',
          margin: 0,
          backgroundColor: scheme.background,
          // Safe area для мобильных
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        },
        '#root': {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
        // Улучшаем touch experience
        '*': {
          WebkitTapHighlightColor: 'transparent',
        },
        // Скроллбар в стиле MD3
        '::-webkit-scrollbar': {
          width: 8,
          height: 8,
        },
        '::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '::-webkit-scrollbar-thumb': {
          background: scheme.outlineVariant,
          borderRadius: 4,
          '&:hover': {
            background: scheme.outline,
          },
        },
      },
    },
    
    // Button - MD3 style
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: shape.borderRadiusFull, // Pill shape
          textTransform: 'none',
          fontWeight: 500,
          padding: '10px 24px',
          minHeight: 40,
          // Touch target
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            minWidth: 48,
            minHeight: 48,
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: elevationShadows[1],
          },
        },
        outlined: {
          borderColor: scheme.outline,
        },
        text: {
          '&:hover': {
            backgroundColor: alpha(scheme.primary, 0.08),
          },
        },
        sizeSmall: {
          padding: '6px 16px',
          minHeight: 32,
        },
        sizeLarge: {
          padding: '12px 32px',
          minHeight: 48,
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    
    // Fab - MD3 style
    MuiFab: {
      styleOverrides: {
        root: {
          borderRadius: shape.borderRadiusLarge,
          boxShadow: elevationShadows[3],
          '&:hover': {
            boxShadow: elevationShadows[4],
          },
        },
        extended: {
          borderRadius: shape.borderRadiusLarge,
          padding: '0 20px',
        },
      },
    },
    
    // Card - MD3 filled card style
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: shape.borderRadiusMedium,
          boxShadow: 'none',
          backgroundColor: scheme.surfaceContainerLow,
          border: 'none',
          // Elevated variant
          '&.MuiCard-elevated': {
            backgroundColor: scheme.surface,
            boxShadow: elevationShadows[1],
          },
          // Outlined variant
          '&.MuiCard-outlined': {
            backgroundColor: scheme.surface,
            border: `1px solid ${scheme.outlineVariant}`,
          },
        },
      },
      defaultProps: {
        elevation: 0,
      },
    },
    
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 16,
          '&:last-child': {
            paddingBottom: 16,
          },
        },
      },
    },
    
    // Paper
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: shape.borderRadiusMedium,
        },
        elevation1: {
          boxShadow: elevationShadows[1],
        },
        elevation2: {
          boxShadow: elevationShadows[2],
        },
        elevation3: {
          boxShadow: elevationShadows[3],
        },
      },
    },
    
    // TextField - MD3 outlined style
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },
    
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: shape.borderRadiusSmall,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: scheme.onSurface,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
          },
        },
        notchedOutline: {
          borderColor: scheme.outline,
        },
      },
    },
    
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: scheme.onSurfaceVariant,
        },
      },
    },
    
    // Chip - MD3 style
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: shape.borderRadiusSmall,
          height: 32,
          fontWeight: 500,
        },
        filled: {
          backgroundColor: scheme.secondaryContainer,
          color: scheme.onSecondaryContainer,
          '&:hover': {
            backgroundColor: alpha(scheme.secondaryContainer, 0.8),
          },
        },
        outlined: {
          borderColor: scheme.outline,
        },
      },
    },
    
    // List
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: shape.borderRadiusFull,
          margin: '2px 8px',
          padding: '8px 16px',
          '&.Mui-selected': {
            backgroundColor: scheme.secondaryContainer,
            color: scheme.onSecondaryContainer,
            '&:hover': {
              backgroundColor: alpha(scheme.secondaryContainer, 0.8),
            },
            '& .MuiListItemIcon-root': {
              color: scheme.onSecondaryContainer,
            },
          },
        },
      },
    },
    
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 40,
          color: scheme.onSurfaceVariant,
        },
      },
    },
    
    // AppBar - MD3 surface style
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: scheme.surface,
          color: scheme.onSurface,
          boxShadow: 'none',
          borderBottom: `1px solid ${scheme.outlineVariant}`,
        },
      },
      defaultProps: {
        elevation: 0,
        color: 'default',
      },
    },
    
    // Drawer
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: scheme.surfaceContainerLow,
          borderRight: 'none',
        },
      },
    },
    
    // Dialog - MD3 style
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: shape.borderRadiusExtraLarge,
          backgroundColor: scheme.surfaceContainerHigh,
        },
      },
    },
    
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: 24,
          paddingBottom: 16,
        },
      },
    },
    
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 24,
          paddingTop: 0,
        },
      },
    },
    
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 24,
          paddingTop: 16,
        },
      },
    },
    
    // Table
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: scheme.outlineVariant,
          padding: '12px 16px',
        },
        head: {
          fontWeight: 500,
          color: scheme.onSurfaceVariant,
          backgroundColor: scheme.surfaceContainerLow,
        },
      },
    },
    
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha(scheme.onSurface, 0.04),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(scheme.primary, 0.08),
            '&:hover': {
              backgroundColor: alpha(scheme.primary, 0.12),
            },
          },
        },
      },
    },
    
    // Tooltip
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: scheme.inverseSurface,
          color: scheme.inverseOnSurface,
          borderRadius: shape.borderRadiusSmall,
          fontSize: '0.75rem',
          padding: '8px 12px',
        },
      },
    },
    
    // Snackbar
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: scheme.inverseSurface,
          color: scheme.inverseOnSurface,
          borderRadius: shape.borderRadiusSmall,
        },
      },
    },
    
    // Alert
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: shape.borderRadiusMedium,
        },
        standardSuccess: {
          backgroundColor: scheme.successContainer,
          color: scheme.onSuccessContainer,
        },
        standardError: {
          backgroundColor: scheme.errorContainer,
          color: scheme.onErrorContainer,
        },
        standardWarning: {
          backgroundColor: scheme.warningContainer,
          color: scheme.onWarningContainer,
        },
        standardInfo: {
          backgroundColor: scheme.tertiaryContainer,
          color: scheme.onTertiaryContainer,
        },
      },
    },
    
    // Avatar
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: scheme.primaryContainer,
          color: scheme.onPrimaryContainer,
        },
      },
    },
    
    // IconButton
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: shape.borderRadiusFull,
          // Touch target
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            minWidth: 48,
            minHeight: 48,
          },
        },
      },
    },
    
    // Switch - MD3 style
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 52,
          height: 32,
          padding: 0,
          // size="small": пропорционально уменьшаем трек/бегунок/сдвиг.
          // Селекторы под .MuiSwitch-sizeSmall (класс на root) специфичнее
          // глобальных .thumb / .switchBase.Mui-checked, поэтому переопределяют их.
          '&.MuiSwitch-sizeSmall': {
            width: 40,
            height: 24,
            '& .MuiSwitch-switchBase': {
              padding: 4,
              '&.Mui-checked': {
                transform: 'translateX(16px)',
              },
            },
            '& .MuiSwitch-thumb': {
              width: 16,
              height: 16,
            },
          },
        },
        switchBase: {
          padding: 4,
          '&.Mui-checked': {
            transform: 'translateX(20px)',
            '& + .MuiSwitch-track': {
              backgroundColor: scheme.primary,
              opacity: 1,
            },
            '& .MuiSwitch-thumb': {
              backgroundColor: scheme.onPrimary,
            },
          },
        },
        thumb: {
          width: 24,
          height: 24,
          backgroundColor: scheme.outline,
        },
        track: {
          borderRadius: 16,
          backgroundColor: scheme.surfaceContainerHighest,
          border: `2px solid ${scheme.outline}`,
          opacity: 1,
        },
      },
    },
    
    // Tabs - MD3 style
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 48,
          '&.Mui-selected': {
            color: scheme.primary,
          },
        },
      },
    },
    
    // Badge
    MuiBadge: {
      styleOverrides: {
        badge: {
          borderRadius: shape.borderRadiusFull,
          fontWeight: 500,
        },
      },
    },
    
    // Pagination
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: shape.borderRadiusFull,
          '&.Mui-selected': {
            backgroundColor: scheme.secondaryContainer,
            color: scheme.onSecondaryContainer,
          },
        },
      },
    },
  },
});

export default theme;
